import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { HoverCard } from "./ResultsPrimitives.jsx";
import DashboardReportActions from "./DashboardReportActions.jsx";
import DashboardCalculationHover, { DashboardCalculationHint, DASHBOARD_CALCULATIONS } from "./DashboardCalculationHover.jsx";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { focusBillingDialogElement, handleBillingDialogKeyDown } from "../utils/billingDialogAccessibility.js";
import { formatFleetDate, formatFleetHours, formatFleetPercent, hasFleetHours, isLowUtilizationDay, LOW_UTILIZATION_THRESHOLD_PCT, summarizeFleetUtilization } from "../utils/fleetUtilizationData.js";
import "./DashboardCharts.css";
import "./FleetUtilizationCard.css";

const TITLE = "Utilisation quotidienne des camions";
const RULE = "Chaque camion compte à 100 % dès 90 % de ses heures max.";

function DayMetrics({ day }) {
  const data = day.fleetUtilization;
  return <>
    <dl className="fleet-day-metrics">
      {[
        ["Heures planifiées", formatFleetHours(data?.workedMinutes)],
        ["Heures max", formatFleetHours(data?.maxWorkMinutes)],
        ["Utilisation retenue", formatFleetPercent(data?.retainedPct)],
        ["Camions actifs", data?.activeTruckCount ?? "n/d"],
        ["Camions à 100 %", data?.fullTruckCount ?? "n/d"],
        ["Désactivés du dispatch · exclus", data?.disabledTruckCount ?? "n/d"],
      ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl>
    {data?.invalidTruckCount > 0 && <p className="fleet-partial">Données partielles : {data.invalidTruckCount} camion(s) hors calcul, heures ou maximum invalides.</p>}
    {!hasFleetHours(data) && <p className="fleet-muted">Données indisponibles pour le calcul des heures.</p>}
  </>;
}

export function TruckDetailDialog({ day, onClose }) {
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const titleId = React.useId();
  React.useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    focusBillingDialogElement(closeRef.current);
    return () => {
      document.body.style.overflow = previousOverflow;
      focusBillingDialogElement(previousFocus);
    };
  }, []);
  return createPortal(<div className="fleet-dialog-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} className="fleet-dialog rounded-[4px]"
      onKeyDown={(event) => handleBillingDialogKeyDown(event, { activeElement: document.activeElement, dialog: dialogRef.current, onClose })}>
      <header className="fleet-card-header">
        <div><h2 id={titleId}>Camions du {formatFleetDate(day)}</h2><p className="fleet-muted">Du moins utilisé au plus utilisé · Heures planifiées</p></div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Fermer le détail des camions"
          className="rounded-[4px] border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-accent"><X size={18} /></button>
      </header>
      <DayMetrics day={day} />
      <p className="fleet-rule">{RULE}</p>
      <div className="fleet-table-scroll">
        <table className="fleet-trucks-table">
          <caption className="sr-only">Heures planifiées et utilisation des camions actifs du {formatFleetDate(day)}</caption>
          <thead><tr><th scope="col">Camion</th><th scope="col">Planifiées</th><th scope="col">Maximum</th><th scope="col">Taux retenu</th></tr></thead>
          <tbody>{(day.fleetUtilization?.trucks || []).map((truck) => <tr key={truck.truckId}>
            <th scope="row">{truck.name}
              {truck.overtimeMinutes > 0 && <span className="fleet-overtime">Dépassement : <span className="fleet-duration">+{formatFleetHours(truck.overtimeMinutes)}</span></span>}
              {truck.retainedPct === null && <span className="fleet-muted fleet-row-note">Hors calcul · données incomplètes</span>}
            </th>
            <td>{formatFleetHours(truck.workedMinutes)}</td><td>{formatFleetHours(truck.maxWorkMinutes)}</td>
            <td><span className={truck.retainedPct === 100 ? "fleet-full rounded-[4px]" : ""}>{formatFleetPercent(truck.retainedPct)}</span></td>
          </tr>)}</tbody>
        </table>
      </div>
      {!day.fleetUtilization?.trucks.length && <p className="fleet-muted">Aucun camion actif disponible dans les données de cette journée.</p>}
    </section>
  </div>, getReactPortalRoot());
}

// `overlay` (dashboard page) draws the planned hours inside the max-hours bar; days outside `selectedDates` are context.
function HoursChart({ days, onSelectDay, overlay = false, plotHeight = 172, selectedDates = null }) {
  const scrollRef = React.useRef(null);
  const [measuredWidth, setMeasuredWidth] = React.useState(680);
  React.useEffect(() => {
    const observer = new ResizeObserver(([entry]) => { if (entry.contentRect.width > 0) setMeasuredWidth(entry.contentRect.width); });
    observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, []);
  const minimumWidth = measuredWidth < 600 ? Math.max(480, days.length * 28 + 56) : 0;
  const width = Math.max(minimumWidth, measuredWidth);
  const left = 48, right = width - 8, top = 12, bottom = top + plotHeight, height = bottom + 30;
  const slot = (right - left) / days.length;
  const peak = Math.max(1, ...days.flatMap((day) => [day.fleetUtilization?.workedMinutes || 0, day.fleetUtilization?.maxWorkMinutes || 0])) / 60;
  const magnitude = 10 ** Math.floor(Math.log10(peak / 4));
  const step = Math.ceil(peak / 4 / magnitude) * magnitude;
  const maximum = step * 4;
  const y = (minutes) => bottom - (minutes / 60 / maximum) * (bottom - top);
  const labelEvery = Math.max(1, Math.ceil(days.length / 7));
  return <div ref={scrollRef} className="fleet-chart-scroll" data-dashboard-chart-frame="true" role="region" aria-label="Heures planifiées et heures max par journée">
    <div className="fleet-chart-canvas" style={{ minWidth: minimumWidth }}>
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((index) => {
          const value = index * step;
          const position = y(value * 60);
          return <g key={index}><line x1={left} x2={right} y1={position} y2={position} className="fleet-chart-grid" />
            <text x={left - 7} y={position + 4} textAnchor="end" className="fleet-chart-label">{Number(value.toPrecision(3)).toLocaleString("fr-CA")} h</text></g>;
        })}
        {days.map((day, index) => {
          const data = day.fleetUtilization;
          const center = left + slot * (index + 0.5);
          const barWidth = Math.min(20, slot * 0.3);
          const overlayWidth = Math.min(26, slot * 0.6);
          const context = selectedDates && !selectedDates.has(day.dispatchDate) ? "true" : undefined;
          return <g key={day.dispatchDate} data-context={context}>
            {hasFleetHours(data) ? overlay ? <>
              <rect data-fleet-bar="maximum" x={center - overlayWidth / 2} y={y(data.maxWorkMinutes)} width={overlayWidth} height={bottom - y(data.maxWorkMinutes)} rx="2" className="fleet-bar-max" />
              <rect data-fleet-bar="planned" x={center - overlayWidth / 2} y={y(data.workedMinutes)} width={overlayWidth} height={bottom - y(data.workedMinutes)} rx="2"
                className={isLowUtilizationDay(day) ? "fleet-bar-planned fleet-bar-low" : "fleet-bar-planned"} />
            </> : <>
              <rect data-fleet-bar="planned" x={center - barWidth - 1} y={y(data.workedMinutes)} width={barWidth} height={bottom - y(data.workedMinutes)} rx="2" className="fleet-bar-planned" />
              <rect data-fleet-bar="maximum" x={center + 1} y={y(data.maxWorkMinutes)} width={barWidth} height={bottom - y(data.maxWorkMinutes)} rx="2" className="fleet-bar-max" />
            </> : <text x={center} y={bottom - 6} textAnchor="middle" className="fleet-chart-label">—</text>}
            {(index % labelEvery === 0 || (index === days.length - 1 && index % labelEvery >= labelEvery / 2)) &&
              <text x={center} y={bottom + 21} textAnchor={index === 0 ? "start" : index === days.length - 1 ? "end" : "middle"} className="fleet-chart-label">{formatFleetDate(day, true)}</text>}
          </g>;
        })}
      </svg>
      {days.map((day, index) => <HoverCard key={day.dispatchDate} title={formatFleetDate(day)} content={<><DayMetrics day={day} /><p className="fleet-rule">{DASHBOARD_CALCULATIONS["Utilisation retenue"]}</p></>}
        referenceAsChild closeOnReferenceClick flat accentColor="var(--color-accent)" placement="top" maxWidth={340} delay={100}>
        <button type="button" className="fleet-day-target rounded-[4px]" data-fleet-day={day.dispatchDate}
          style={{ left: `${(left + slot * index) / width * 100}%`, width: `${slot / width * 100}%`, top: `${top / height * 100}%`, height: `${(bottom - top) / height * 100}%` }}
          aria-label={`${formatFleetDate(day)} : ${formatFleetHours(day.fleetUtilization?.workedMinutes)} planifiées sur ${formatFleetHours(day.fleetUtilization?.maxWorkMinutes)}, utilisation retenue ${formatFleetPercent(day.fleetUtilization?.retainedPct)}. Voir les camions.`}
          aria-haspopup="dialog" onClick={() => onSelectDay(day)} />
      </HoverCard>)}
    </div>
  </div>;
}

// `compact` is passed by the dashboard page only: reports keep the explanatory texts.
export default function FleetUtilizationCard({ period = { days: [] }, compact = false, contextDays = null }) {
  const [selectedDay, setSelectedDay] = React.useState(null);
  React.useEffect(() => { setSelectedDay(null); }, [period.key]);
  const days = period.days || [];
  const summary = summarizeFleetUtilization(days);
  // The page keeps the loaded days as context around the selected period.
  const chartDays = compact && contextDays?.length ? contextDays : days;
  const chartSummary = chartDays === days ? summary : summarizeFleetUtilization(chartDays);
  const legend = <div className="fleet-legend"><span><i className="fleet-swatch-planned" />{compact ? "Planifiées" : "Heures planifiées"}</span>
    {compact && <span><i className="fleet-swatch-low" />Sous {LOW_UTILIZATION_THRESHOLD_PCT} %</span>}
    <span><i className="fleet-swatch-max" />{compact ? "Max" : "Heures max"}</span></div>;
  return <section className="dashboard-card fleet-utilization-card rounded-[4px]" aria-label={TITLE}>
    <header className="dashboard-card-header"><div><h2>{TITLE}</h2><p className="fleet-muted">{period.label} · {compact ? "cliquez une journée pour voir les camions" : "Données planifiées"}</p></div>{compact ? <div className="fleet-card-tools">{legend}<DashboardReportActions title={TITLE} /></div> : <DashboardReportActions title={TITLE} />}</header>
    {!compact && <dl className="fleet-summary">
      {[
        [period.key === "day" ? "Heures planifiées" : "Heures planifiées / dispatch", formatFleetHours(summary.workedMinutes)],
        [period.key === "day" ? "Heures max" : "Heures max / dispatch", formatFleetHours(summary.maxWorkMinutes)],
        [period.key === "day" ? "Utilisation retenue" : "Utilisation globale", formatFleetPercent(summary.retainedPct), "fleet-retained"],
      ].map(([label, value, className]) => <DashboardCalculationHover key={label} label={label} value={value}>
        <div><dt>{label}<DashboardCalculationHint /></dt><dd className={className}>{value}</dd></div>
      </DashboardCalculationHover>)}
    </dl>}
    {!compact && period.key !== "day" && <p className="fleet-muted">Heures moyennes par dispatch · {summary.validDayCount} dispatch{summary.validDayCount > 1 ? "s" : ""} calculable{summary.validDayCount > 1 ? "s" : ""}. Taux global pondéré par les heures max, avec la règle des <span className="fleet-duration">90 %</span>.</p>}
    {!compact && <p className="fleet-rule">{RULE}</p>}
    {!compact && legend}
    {chartSummary.validDayCount > 0 ? <HoursChart days={chartDays} onSelectDay={setSelectedDay} overlay={compact} plotHeight={compact ? 190 : undefined}
      selectedDates={compact ? new Set(days.map((day) => day.dispatchDate)) : null} />
      : <div className="fleet-empty" role="status">Données indisponibles pour le calcul des heures.
        {days.map((day) => <button key={day.dispatchDate} type="button" className="fleet-detail-link" onClick={() => setSelectedDay(day)}>Voir les camions du {formatFleetDate(day)}</button>)}
      </div>}
    {summary.isPartial && <p className="fleet-partial">Données partielles · {summary.validDayCount}/{summary.totalDayCount} journées calculables{summary.invalidTruckCount > 0 ? ` · ${summary.invalidTruckCount} camion(s)-journée hors calcul` : ""}. Les indicateurs portent sur les données calculables.</p>}
    {!compact && <p className="fleet-chart-help">Sélectionnez une journée pour voir les camions.<span> Sur petit écran, faites défiler le graphique.</span></p>}
    {selectedDay && <TruckDetailDialog day={selectedDay} onClose={() => setSelectedDay(null)} />}
  </section>;
}
